import { useCallback } from 'react';

import { ChevronDown, Trash2 } from 'lucide-react';
import { UseFormReturn, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { TableCell, TableRow } from '@/components/ui/table';
import {
  formatAmountWithCurrencyCode,
  formatTrimmedDecimal,
  parseFormNumber,
  roundToPrecision,
} from '@/helpers/finance';
import { cn } from '@/lib/utils';
import { SingleSelectOption } from '@/modules/common';
import { InputFieldWithCurrency } from '@/modules/common/components/molecules/inputs/input-field-with-currency';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import { RateMask } from '@/modules/common/utils/rate-mask';
import {
  createScrollableOptionRenderer,
  createScrollableTriggerRenderer,
  LineValidationTooltip,
} from '@/modules/finance/common';
import { FINANCE_QUANTITY_MAX_DECIMAL_PLACES } from '@/modules/finance/common/constants/line-item-validation.constants';
import {
  FINANCE_MAX_INTEGER_DIGITS,
  FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES,
  FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES,
} from '@/modules/finance/common/constants/rounding.constants';
import { getShortProjects } from '@/modules/projects';

import { TIME_OFF_SOURCE_LABEL_KEYS } from '../../constants';
import {
  BillDetailsItemType,
  BillDetailsTimeOffTypeSource,
  BillTimeOffSource,
  BillTimeOffSourceType,
} from '../../types';
import { getBillLineAmount } from '../../utils';
import {
  BillLineValidationIssue,
  UPDATE_BILL_LINE_MESSAGE_KEY_BY_ISSUE_CODE,
  UpdateBillFormValues,
  UpdateBillItemFormValues,
} from '../../validations';

const renderProjectOption = createScrollableOptionRenderer({ showColorDot: true });
const renderProjectTrigger = createScrollableTriggerRenderer({ showColorDot: true });
const BILL_LINE_ITEM_NUMERIC_FIELDS = {
  Quantity: 'quantity',
  UnitPrice: 'unitPrice',
} as const;
const renderProjectTriggerWithChevron = (
  selectedOption: SingleSelectOption | null,
  placeholder: string
) => (
  <div className="flex min-w-0 flex-1 items-center gap-2">
    {renderProjectTrigger(selectedOption, placeholder)}
    <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
  </div>
);

interface BillLineItemRowProps {
  index: number;
  itemName: string | null | undefined;
  itemAvatarUrl: string | null | undefined;
  itemColorHex: string | null | undefined;
  timeOffSources: BillDetailsTimeOffTypeSource[];
  form: UseFormReturn<UpdateBillFormValues>;
  isReadOnly: boolean;
  currencyCode: string;
  validationIssues: BillLineValidationIssue[];
  onRemove: () => void;
}

/** Keeps each line's form subscriptions isolated from the details form. */
export function BillLineItemRow({
  index,
  itemName,
  itemAvatarUrl,
  itemColorHex,
  timeOffSources,
  form,
  isReadOnly,
  currencyCode,
  validationIssues,
  onRemove,
}: BillLineItemRowProps) {
  const { t } = useTranslation(['authenticated.bills', 'common']);
  const item = useWatch({ control: form.control, name: `items.${index}` });
  const { errors } = useFormState({ control: form.control, name: `items.${index}` });
  const itemErrors = isReadOnly ? undefined : errors.items?.[index];
  const selectedTimeOff = timeOffSources.find(
    timeOff => timeOff.timeOffTypeId === Number(item.entityId)
  );
  const isOverhead = item.entityType === BillDetailsItemType.Overhead;
  const validationMessages = [
    ...new Set([
      ...validationIssues.map(issue => t(UPDATE_BILL_LINE_MESSAGE_KEY_BY_ISSUE_CODE[issue.code])),
      ...(itemErrors?.entityType?.message ? [t(itemErrors.entityType.message)] : []),
    ]),
  ];

  return (
    <TableRow>
      <BillLineItemReferenceCell
        index={index}
        item={item}
        itemName={itemName}
        itemAvatarUrl={itemAvatarUrl}
        itemColorHex={itemColorHex}
        timeOffSources={timeOffSources}
        form={form}
        isReadOnly={isReadOnly}
        hasEntityIdError={Boolean(itemErrors?.entityId)}
        hasDescriptionError={Boolean(itemErrors?.description)}
        validationMessages={validationMessages}
      />
      <BillLineItemTimeOffSourceCell
        index={index}
        form={form}
        isTimeOffType={item.entityType === BillDetailsItemType.TimeOffType}
        selectedTimeOff={selectedTimeOff}
        isReadOnly={isReadOnly}
      />
      <BillLineItemNumericCell
        field={BILL_LINE_ITEM_NUMERIC_FIELDS.Quantity}
        index={index}
        form={form}
        isOverhead={isOverhead}
        isReadOnly={isReadOnly}
        currencyCode={currencyCode}
        isMasked={RateMask.isMaskedStringValue(item.quantity)}
        value={item.quantity}
        hasError={Boolean(itemErrors?.quantity)}
      />
      <BillLineItemNumericCell
        field={BILL_LINE_ITEM_NUMERIC_FIELDS.UnitPrice}
        index={index}
        form={form}
        isOverhead={isOverhead}
        isReadOnly={isReadOnly}
        currencyCode={currencyCode}
        isMasked={RateMask.isMaskedStringValue(item.unitPrice)}
        value={item.unitPrice}
        hasError={Boolean(itemErrors?.unitPrice)}
      />
      <BillLineItemAmountCell
        item={item}
        isOverhead={isOverhead}
        isReadOnly={isReadOnly}
        currencyCode={currencyCode}
      />
      {!isReadOnly && (
        <TableCell>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </TableCell>
      )}
    </TableRow>
  );
}

interface BillLineItemReferenceCellProps {
  index: number;
  item: UpdateBillItemFormValues;
  itemName: string | null | undefined;
  itemAvatarUrl: string | null | undefined;
  itemColorHex: string | null | undefined;
  timeOffSources: BillDetailsTimeOffTypeSource[];
  form: UseFormReturn<UpdateBillFormValues>;
  isReadOnly: boolean;
  hasEntityIdError: boolean;
  hasDescriptionError: boolean;
  validationMessages: string[];
}

/**
 * Renders the primary reference cell for a bill line item.
 * The control shown depends on the line's entity type:
 * - Project: paginated project select
 * - TimeOffType: time-off type select that also seeds quantity/rate/amount from its source
 * - Client/Unlinked (or any type requiring a raw reference): plain entityId input
 * - Overhead (or default): free-text description input
 *
 * Selecting a time-off type initializes the line from its preferred available source.
 */
function BillLineItemReferenceCell({
  index,
  item,
  itemName,
  itemAvatarUrl,
  itemColorHex,
  timeOffSources,
  form,
  isReadOnly,
  hasEntityIdError,
  hasDescriptionError,
  validationMessages,
}: BillLineItemReferenceCellProps) {
  const { t } = useTranslation('authenticated.bills');
  const selectedTimeOff = timeOffSources.find(
    timeOff => timeOff.timeOffTypeId === Number(item.entityId)
  );
  const requiresReference =
    item.entityType === BillDetailsItemType.Project ||
    item.entityType === BillDetailsItemType.TimeOffType;
  const isOverhead = item.entityType === BillDetailsItemType.Overhead;
  const loadProjects = useCallback(
    (cursor?: number | null) => getShortProjects({ pagination: { cursor } }),
    []
  );
  const searchProjects = useCallback(
    (searchTerm: string, cursor?: number | null) =>
      getShortProjects({ filter: { searchString: searchTerm }, pagination: { cursor } }),
    []
  );

  const updateTimeOffSource = (timeOffTypeId: string) => {
    const timeOff = timeOffSources.find(
      candidate => candidate.timeOffTypeId === Number(timeOffTypeId)
    );
    const source =
      timeOff?.hoursSources.find(
        candidate => candidate.sourceType === BillTimeOffSourceType.APPROVED
      ) ?? timeOff?.hoursSources[0];
    if (!source) return;

    form.setValue(`items.${index}.quantity`, source.hours.toString(), { shouldDirty: true });
    form.setValue(`items.${index}.unitPrice`, source.averageRate.toString(), { shouldDirty: true });
    form.setValue(`items.${index}.amount`, source.amount.toString(), { shouldDirty: true });
    form.setValue(`items.${index}.timeOffSourceType`, source.sourceType, { shouldDirty: true });
  };

  return (
    <TableCell>
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0 flex-1">
          {item.entityType === BillDetailsItemType.Project ? (
            <SingleSelectWithPagination<UpdateBillFormValues>
              name={`items.${index}.entityId`}
              control={form.control}
              placeholder={t('lineItemActions.selectProject')}
              queryKey={['bill-edit-projects']}
              loadItems={loadProjects}
              searchItems={searchProjects}
              renderOption={renderProjectOption}
              renderTrigger={renderProjectTriggerWithChevron}
              initialSelectedItem={
                itemName && item.entityId
                  ? {
                      id: item.entityId,
                      name: itemName,
                      avatarLink: itemAvatarUrl ?? undefined,
                      colorHex: itemColorHex ?? undefined,
                    }
                  : undefined
              }
              disabled={isReadOnly}
              showFormMessage={false}
            />
          ) : item.entityType === BillDetailsItemType.TimeOffType ? (
            <SingleSelect
              name={`items.${index}.entityId`}
              control={form.control}
              placeholder={t('details.edit.placeholders.timeOffType')}
              options={timeOffSources
                .filter(timeOff => timeOff.isBillable)
                .map(timeOff => ({
                  value: timeOff.timeOffTypeId.toString(),
                  label: timeOff.timeOffTypeName,
                }))}
              disabled={isReadOnly}
              searchable={false}
              showFormMessage={false}
              initialSelectedItem={
                selectedTimeOff
                  ? {
                      value: selectedTimeOff.timeOffTypeId.toString(),
                      label: selectedTimeOff.timeOffTypeName,
                    }
                  : itemName && item.entityId
                    ? { value: item.entityId, label: itemName }
                    : null
              }
              onChange={updateTimeOffSource}
            />
          ) : requiresReference ? (
            <FormField
              control={form.control}
              name={`items.${index}.entityId`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={isReadOnly}
                      placeholder={t('details.edit.items.entityId')}
                      className={cn(hasEntityIdError && 'border-destructive')}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          ) : (
            <FormField
              control={form.control}
              name={`items.${index}.description`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={isReadOnly || !isOverhead}
                      placeholder={t('details.edit.items.description')}
                      className={cn(hasDescriptionError && 'border-destructive')}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
        </div>
        <div className="flex h-4 w-4 shrink-0 items-center justify-center">
          {!isReadOnly && <LineValidationTooltip messages={validationMessages} />}
        </div>
      </div>
    </TableCell>
  );
}

/**
 * Renders the time-off source cell for a bill line item.
 * Only relevant when the line's entity type is TimeOffType and a matching
 * time-off source was found — otherwise shows a placeholder dash.
 * Lets the user pick which hours source (e.g. approved vs. requested) to bill from.
 *
 * A source change replaces all values that are calculated from that source
 * (quantity, unit price, and amount).
 */
function BillLineItemTimeOffSourceCell({
  index,
  form,
  isTimeOffType,
  selectedTimeOff,
  isReadOnly,
}: {
  index: number;
  form: UseFormReturn<UpdateBillFormValues>;
  isTimeOffType: boolean;
  selectedTimeOff: BillDetailsTimeOffTypeSource | undefined;
  isReadOnly: boolean;
}) {
  if (!isTimeOffType || !selectedTimeOff)
    return (
      <TableCell>
        <span className="text-muted-foreground">-</span>
      </TableCell>
    );

  return (
    <TableCell>
      <TimeOffSourceSelect
        index={index}
        form={form}
        sources={selectedTimeOff.hoursSources}
        disabled={isReadOnly || !selectedTimeOff.isBillable}
        onChange={sourceType => {
          const source = selectedTimeOff.hoursSources.find(
            candidate => candidate.sourceType === sourceType
          );
          if (!source) return;
          form.setValue(`items.${index}.quantity`, source.hours.toString(), { shouldDirty: true });
          form.setValue(`items.${index}.unitPrice`, source.averageRate.toString(), {
            shouldDirty: true,
          });
          form.setValue(`items.${index}.amount`, source.amount.toString(), { shouldDirty: true });
        }}
      />
    </TableCell>
  );
}

/** Masked source values render a static, disabled input; editable values use the currency field. */
function BillLineItemNumericCell({
  field,
  index,
  form,
  isOverhead,
  isReadOnly,
  currencyCode,
  isMasked,
  value,
  hasError,
}: {
  field: (typeof BILL_LINE_ITEM_NUMERIC_FIELDS)[keyof typeof BILL_LINE_ITEM_NUMERIC_FIELDS];
  index: number;
  form: UseFormReturn<UpdateBillFormValues>;
  isOverhead: boolean;
  isReadOnly: boolean;
  currencyCode: string;
  isMasked: boolean;
  value: string;
  hasError: boolean;
}) {
  const { t } = useTranslation('authenticated.bills');

  if (isOverhead && field === BILL_LINE_ITEM_NUMERIC_FIELDS.Quantity)
    return (
      <TableCell>
        <span className="tabular-nums">{formatTrimmedDecimal(1)}</span>
      </TableCell>
    );

  if (isOverhead)
    return (
      <TableCell>
        <div className={hasError ? '[&_input]:border-destructive' : ''}>
          <InputFieldWithCurrency
            name={`items.${index}.unitPrice`}
            control={form.control}
            isReadMode={isReadOnly}
            valueType="string"
            showFormMessage={false}
            suffixText={currencyCode}
            maxDecimalPlaces={FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES}
            maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
            allowNegative
            allowEmpty
          />
        </div>
      </TableCell>
    );

  if (isMasked)
    return (
      <TableCell>
        <Input
          value={RateMask.applyToString(value)}
          disabled
          aria-label={t(`details.edit.items.${field}`)}
          className={cn('tabular-nums', hasError && 'border-destructive')}
        />
      </TableCell>
    );

  if (field === BILL_LINE_ITEM_NUMERIC_FIELDS.UnitPrice)
    return (
      <TableCell>
        <div className={hasError ? '[&_input]:border-destructive' : ''}>
          <InputFieldWithCurrency
            name={`items.${index}.unitPrice`}
            control={form.control}
            isReadMode={isReadOnly}
            valueType="string"
            showFormMessage={false}
            suffixText={t('common:rateUnits.perHour', { currency: currencyCode })}
            maxDecimalPlaces={FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES}
            maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
          />
        </div>
      </TableCell>
    );

  return (
    <TableCell>
      <div className={hasError ? '[&_input]:border-destructive' : ''}>
        <InputFieldWithCurrency
          name={`items.${index}.${field}`}
          control={form.control}
          isReadMode={isReadOnly}
          valueType="string"
          showFormMessage={false}
          maxDecimalPlaces={FINANCE_QUANTITY_MAX_DECIMAL_PLACES}
          maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
          minValue={0}
        />
      </div>
    </TableCell>
  );
}

/** Amounts are derived from quantity and rate; overheads always use an implicit quantity of one. */
function BillLineItemAmountCell({
  item,
  isOverhead,
  isReadOnly,
  currencyCode,
}: {
  item: UpdateBillItemFormValues;
  isOverhead: boolean;
  isReadOnly: boolean;
  currencyCode: string;
}) {
  if (isReadOnly)
    return (
      <TableCell>
        <span className="font-medium tabular-nums">
          {RateMask.isMaskedStringValue(item.amount)
            ? RateMask.applyToString(item.amount)
            : formatAmountWithCurrencyCode(item.amount, currencyCode)}
        </span>
      </TableCell>
    );
  if (isOverhead)
    return (
      <TableCell>
        <span className="tabular-nums">
          {formatAmountWithCurrencyCode(
            roundToPrecision(
              parseFormNumber(item.unitPrice),
              FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
            ).toString(),
            currencyCode
          )}
        </span>
      </TableCell>
    );

  const amount = roundToPrecision(getBillLineAmount(item), FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES);

  return (
    <TableCell>
      <span className="tabular-nums">
        {formatAmountWithCurrencyCode(amount.toString(), currencyCode)}
      </span>
    </TableCell>
  );
}

/** Limits selection to a source type while exposing its available hours for comparison. */
function TimeOffSourceSelect({
  index,
  form,
  sources,
  disabled,
  onChange,
}: {
  index: number;
  form: UseFormReturn<UpdateBillFormValues>;
  sources: BillTimeOffSource[];
  disabled: boolean;
  onChange: (sourceType: BillTimeOffSourceType) => void;
}) {
  const { t } = useTranslation(['authenticated.bills', 'common']);
  const defaultSource =
    sources.find(source => source.sourceType === BillTimeOffSourceType.APPROVED) ?? sources[0];
  const selectedSourceType = useWatch({
    control: form.control,
    name: `items.${index}.timeOffSourceType`,
  });
  const selectedSource =
    sources.find(source => source.sourceType === selectedSourceType) ?? defaultSource;
  return (
    <SingleSelect
      name={`items.${index}.timeOffSourceType`}
      control={form.control}
      options={sources.map(source => ({
        value: source.sourceType,
        label: t(TIME_OFF_SOURCE_LABEL_KEYS[source.sourceType]),
        customContent: (
          <div className="flex w-full items-center justify-between gap-3">
            <span>{t(TIME_OFF_SOURCE_LABEL_KEYS[source.sourceType])}</span>
            <span>{t('common:timeFormat.hoursOnly', { hours: source.hours })}</span>
          </div>
        ),
      }))}
      disabled={disabled || sources.length < 2}
      searchable={false}
      isClearSelectionEnabled={false}
      initialSelectedItem={
        selectedSource
          ? {
              value: selectedSource.sourceType,
              label: t(TIME_OFF_SOURCE_LABEL_KEYS[selectedSource.sourceType]),
            }
          : null
      }
      onChange={value => onChange(value as BillTimeOffSourceType)}
      renderTrigger={selectedOption =>
        selectedOption ? (
          <div className="w-full truncate text-left">{selectedOption.label}</div>
        ) : undefined
      }
    />
  );
}
